import React, { createContext, useReducer, useCallback, useMemo, useContext } from "react";
import PropTypes from "prop-types";
import uniqueId from "lodash/uniqueId";
import { styled } from "@mui/material";

import { Notification } from "./Notification";

let NotificationsManager = {};
const NotificationsContext = createContext(NotificationsManager);
const notificationsReducer = (store, { op, notification }) => {
  switch (op) {
    case "add":
      return store.concat(notification);
    case "remove":
      return store.filter(item => item.id !== notification);
    default:
      return store;
  }
};

/**
 * Provider component for notifications system
 *
 * @component
 */
const NotificationsProvider = ({ children }) => {
  const [notifications, dispatch] = useReducer(notificationsReducer, []);
  const remove = useCallback(notification => dispatch({ op: "remove", notification }), [dispatch]);
  const add = useCallback(
    (options = {}) => {
      const id = uniqueId("notification-");
      dispatch({ op: "add", notification: { ...options, id } });

      if (options.delay) {
        setTimeout(() => remove(id), options.delay);
      }
    },
    [dispatch, remove]
  );
  NotificationsManager = useMemo(
    () => ({
      notifications,
      add,
      remove
    }),
    [notifications, add, remove]
  );
  return (
    <NotificationsContext.Provider value={NotificationsManager}>
      {children}
    </NotificationsContext.Provider>
  );
};

NotificationsProvider.propTypes = {
  /** Child components that will have access to notifications context */
  children: PropTypes.node.isRequired
};

export const NotificationsStyled = styled("div")({
  position: "fixed",
  top: "10px",
  right: "10px",
  zIndex: "10000",
  display: "flex",
  flexDirection: "column-reverse",
  "> div": {
    marginBottom: 0
  }
});

/**
 * Helper function to add a notification from anywhere in the application
 *
 * @param {Object} options - Configuration options for the notification
 * @param {string} [options.title] - Title of the notification
 * @param {string} [options.message] - Message content
 * @param {string} [options.icon] - Icon name for the notification
 * @param {string} [options.status] - Status information
 * @param {string} [options.stakTrace] - Stack trace for debugging
 * @param {number} [options.delay] - Auto-dismiss delay in milliseconds
 */
const addNotification = (options = {}) => {
  if (NotificationsManager.add) {
    NotificationsManager.add(options);
  }
};

/**
 * Component that renders all active notifications
 *
 * @component
 */
const Notifications = () => {
  const { notifications, remove } = useContext(NotificationsContext);

  return (
    <NotificationsStyled>
      {notifications.map(({ id, ...options }) => (
        <Notification key={id} {...options} onClose={() => remove(id)} />
      ))}
    </NotificationsStyled>
  );
};

export { NotificationsContext, NotificationsProvider, Notifications, addNotification };
